import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import type {
  FormChecklistItem,
  FormChecklistValue,
} from '@/features/tasks/task-dialogs/fields/FormChecklistBlock';
import { useVoiceRecorder } from '@/pages/task-redesign/model/useVoiceRecorder';
import { fileToBase64Content } from '@/shared/utils/fileToBase64Content';
import { uidNumber } from '@/features/tasks/mobile-calendar/utils/uidNumber';

import type { PersonItem } from '../../types';
import { checklistHours, formatHours } from './checklistModel';
import { ACCEPT_BY_KIND, type AttachKind } from './RAttachMenu';
import { RChecklistRow, type ItemCtx } from './RChecklistRow';

interface Props {
  value: FormChecklistValue;
  onChange: React.Dispatch<React.SetStateAction<FormChecklistValue>>;
  people: PersonItem[];
  /** Скрыть соисполнителей пункта — у создания задачи v2 их нет в контракте. */
  hideMembers?: boolean;
  disabled?: boolean;
}

const EMPTY: FormChecklistValue = { checklists: [], items: [] };

/** Чек-листы формы создания: вкладки со сводкой, пункты с временем, файлами,
 *  комментариями, голосовыми, соисполнителями и подпунктами, композер снизу.
 *  Данные локальные (задачи ещё нет) — всё лежит в значении формы. */
export function RChecklist({
  value,
  onChange,
  people,
  hideMembers,
  disabled,
}: Props) {
  const checklists = value?.checklists ?? EMPTY.checklists;
  const items = value?.items ?? EMPTY.items;

  const [activeIdRaw, setActiveId] = useState<number | null>(null);
  const activeId = checklists.some((c) => c.id === activeIdRaw)
    ? activeIdRaw
    : (checklists[0]?.id ?? null);

  const [rename, setRename] = useState<{ id: number; draft: string } | null>(
    null
  );
  const [textEdit, setTextEdit] = useState<{
    id: number;
    draft: string;
  } | null>(null);
  const [hoursEdit, setHoursEdit] = useState<{
    id: number;
    draft: string;
  } | null>(null);
  const [actionsFor, setActionsFor] = useState<number | null>(null);
  const [fileMenuFor, setFileMenuFor] = useState<number | null>(null);
  const [pickerFor, setPickerFor] = useState<number | null>(null);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [recFor, setRecFor] = useState<number | null>(null);
  const [newItem, setNewItem] = useState('');
  const [newHours, setNewHours] = useState('');

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachTargetRef = useRef<number | null>(null);
  const recTargetRef = useRef<number | null>(null);
  const newItemRef = useRef<HTMLTextAreaElement | null>(null);
  const newHoursRef = useRef<HTMLInputElement | null>(null);

  const patch = (
    id: number,
    fn: (i: FormChecklistItem) => Partial<FormChecklistItem>
  ) =>
    onChange((prev) => ({
      checklists: prev.checklists,
      items: prev.items.map((x) => (x.id === id ? { ...x, ...fn(x) } : x)),
    }));

  const removeItem = (id: number) =>
    onChange((prev) => ({
      checklists: prev.checklists,
      items: prev.items.filter((x) => x.id !== id && x.parentId !== id),
    }));

  const addChecklist = () => {
    const id = uidNumber();
    onChange((prev) => ({
      checklists: [
        ...prev.checklists,
        { id, title: `Чек-лист ${prev.checklists.length + 1}` },
      ],
      items: prev.items,
    }));
    setActiveId(id);
  };

  const removeChecklist = (id: number) =>
    onChange((prev) => ({
      checklists: prev.checklists.filter((c) => c.id !== id),
      items: prev.items.filter((x) => x.checklistId !== id),
    }));

  const addItem = () => {
    const title = newItem.trim();
    if (!title || activeId === null) {
      return;
    }
    onChange((prev) => ({
      checklists: prev.checklists,
      items: [
        ...prev.items,
        {
          id: uidNumber(),
          checklistId: activeId,
          title,
          isComplete: false,
          parentId: null,
          members: [],
          files: [],
          hours: newHours.trim(),
          notes: [],
        },
      ],
    }));
    setNewItem('');
    setNewHours('');
    window.setTimeout(() => newItemRef.current?.focus(), 0);
  };

  const addSub = (parentId: number) => {
    const parent = items.find((x) => x.id === parentId);
    if (!parent) {
      return;
    }
    const id = uidNumber();
    onChange((prev) => ({
      checklists: prev.checklists,
      items: [
        ...prev.items,
        {
          id,
          checklistId: parent.checklistId,
          title: 'Новый подпункт',
          isComplete: false,
          parentId,
          members: [],
          files: [],
          hours: '',
          notes: [],
        },
      ],
    }));
    setActionsFor(null);
    setTextEdit({ id, draft: 'Новый подпункт' });
  };

  const addNote = (id: number) => {
    patch(id, (i) => ({
      notes: [...(i.notes ?? []), { id: uidNumber(), text: '' }],
    }));
    setActionsFor(null);
  };

  const pickFiles = (id: number, kind: AttachKind) => {
    attachTargetRef.current = id;
    setFileMenuFor(null);
    const input = fileInputRef.current;
    if (!input) {
      return;
    }
    input.accept = ACCEPT_BY_KIND[kind];
    input.click();
  };

  const recorder = useVoiceRecorder({
    onSend: async (blob, filename, durationMs) => {
      const target = recTargetRef.current;
      if (target === null) {
        return;
      }
      const content = await fileToBase64Content(
        new File([blob], filename, { type: blob.type })
      );
      patch(target, (i) => ({
        files: [...(i.files ?? []), { name: filename, content, durationMs }],
      }));
      recTargetRef.current = null;
      setRecFor(null);
    },
  });

  const startRec = (id: number) => {
    recTargetRef.current = id;
    setActionsFor(null);
    setRecFor(id);
    void recorder.start();
  };

  const ctx: ItemCtx = {
    people,
    hideMembers,
    disabled,
    peopleQuery,
    setPeopleQuery,
    textEdit,
    setTextEdit,
    hoursEdit,
    setHoursEdit,
    actionsFor,
    setActionsFor,
    fileMenuFor,
    setFileMenuFor,
    pickerFor,
    setPickerFor,
    recFor,
    recorder,
    startRec,
    patch,
    remove: removeItem,
    addSub,
    addNote,
    pickFiles,
  };

  const roots = useMemo(
    () =>
      items.filter((x) => x.checklistId === activeId && x.parentId === null),
    [items, activeId]
  );
  const subsOf = (id: number) => items.filter((x) => x.parentId === id);

  return (
    <div className="flex flex-col gap-[10px]">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={async (e) => {
          const target = attachTargetRef.current;
          const files = Array.from(e.target.files ?? []);
          e.currentTarget.value = '';
          if (target === null || files.length === 0) {
            return;
          }
          const encoded = await Promise.all(
            files.map(async (f) => ({
              name: f.name,
              content: await fileToBase64Content(f),
            }))
          );
          patch(target, (i) => ({ files: [...(i.files ?? []), ...encoded] }));
          attachTargetRef.current = null;
        }}
      />

      {checklists.length > 0 ? (
        <>
          {/* Вкладки чек-листов */}
          <div className="flex items-center gap-[6px] rounded-[20px] bg-[#F1F3F8] p-[4px]">
            <div className="flex min-w-0 flex-1 items-center gap-[6px] overflow-x-auto [scrollbar-width:none]">
              {checklists.map((c) => {
                const list = items.filter((x) => x.checklistId === c.id);
                const roots0 = list.filter((x) => x.parentId === null);
                const done = roots0.filter((x) => x.isComplete).length;
                const pct = roots0.length
                  ? Math.round((done / roots0.length) * 100)
                  : 0;
                const hrs = checklistHours(value, c.id);
                const act = c.id === activeId;

                if (rename?.id === c.id) {
                  return (
                    <input
                      key={c.id}
                      autoFocus
                      maxLength={20}
                      value={rename.draft}
                      onChange={(e) =>
                        setRename({ id: c.id, draft: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                        if (e.key === 'Escape') {
                          setRename(null);
                        }
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v) {
                          onChange((prev) => ({
                            checklists: prev.checklists.map((x) =>
                              x.id === c.id ? { ...x, title: v } : x
                            ),
                            items: prev.items,
                          }));
                        }
                        setRename(null);
                      }}
                      className="w-[172px] rounded-[16px] border border-[rgba(59,77,127,0.35)] bg-white px-[13px] py-[8px] text-[12px] font-semibold text-[#141E31] outline-none"
                    />
                  );
                }

                return (
                  <button
                    key={c.id}
                    type="button"
                    title="Двойной клик — переименовать"
                    onClick={() => setActiveId(c.id)}
                    onDoubleClick={() =>
                      setRename({ id: c.id, draft: c.title })
                    }
                    className="flex min-w-[172px] flex-[0_0_auto] flex-col items-stretch gap-[6px] whitespace-nowrap rounded-[16px] px-[13px] pb-[9px] pt-[8px]"
                    style={{
                      background: act ? '#3B4D7F' : '#FFFFFF',
                      boxShadow: act
                        ? '0 4px 14px rgba(59,77,127,0.28)'
                        : '0 1px 3px rgba(20,30,49,0.06)',
                    }}
                  >
                    <span className="flex items-center gap-[8px]">
                      <span
                        className="min-w-0 flex-1 overflow-hidden text-ellipsis text-left text-[12px] font-semibold"
                        style={{ color: act ? '#FFFFFF' : '#141E31' }}
                      >
                        {c.title}
                      </span>
                      {hrs > 0 ? (
                        <span
                          className="flex items-center gap-[4px] text-[11px] font-medium"
                          style={{
                            color: act
                              ? 'rgba(255,255,255,0.65)'
                              : 'rgba(20,30,49,0.5)',
                          }}
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 16 16"
                            fill="none"
                            className="shrink-0"
                            aria-hidden
                          >
                            <circle
                              cx="8"
                              cy="8"
                              r="6"
                              stroke={
                                act
                                  ? 'rgba(255,255,255,0.65)'
                                  : 'rgba(20,30,49,0.5)'
                              }
                              strokeWidth={1.4}
                            />
                            <path
                              d="M8 4.8V8l2.2 1.6"
                              stroke={
                                act
                                  ? 'rgba(255,255,255,0.65)'
                                  : 'rgba(20,30,49,0.5)'
                              }
                              strokeWidth={1.5}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {formatHours(hrs)} ч
                        </span>
                      ) : null}
                    </span>

                    <span
                      className="block h-[5px] overflow-hidden rounded-full"
                      style={{
                        background: act
                          ? 'rgba(255,255,255,0.22)'
                          : 'rgba(59,77,127,0.14)',
                      }}
                    >
                      <span
                        className="block h-[5px] rounded-full transition-[width] duration-300"
                        style={{
                          width: `${pct}%`,
                          background: act ? '#7FD3B2' : '#57B792',
                        }}
                      />
                    </span>

                    <span className="flex items-center gap-[8px]">
                      <span
                        className="text-[11px] font-medium"
                        style={{
                          color: act
                            ? 'rgba(255,255,255,0.65)'
                            : 'rgba(20,30,49,0.5)',
                        }}
                      >
                        {done} из {roots0.length} готово
                      </span>
                      <span className="flex-1" />
                      <span
                        className="text-[11px] font-bold"
                        style={{ color: act ? '#7FD3B2' : '#57B792' }}
                      >
                        {pct}%
                      </span>
                      <span
                        title="Удалить чек-лист"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeChecklist(c.id);
                        }}
                        className="flex cursor-pointer items-center justify-center p-[1px]"
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={act ? 'rgba(255,255,255,0.7)' : '#EF4642'}
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M4 7h16M9.5 7V5h5v2M6.5 7l.8 12.2a1.8 1.8 0 0 0 1.8 1.7h5.8a1.8 1.8 0 0 0 1.8-1.7L17.5 7" />
                        </svg>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              title="Добавить чек-лист"
              disabled={disabled}
              onClick={addChecklist}
              className="flex w-[30px] flex-[0_0_30px] items-center justify-center self-stretch rounded-[14px] hover:bg-[rgba(59,77,127,0.06)]"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden
              >
                <path
                  d="M8 3v10M3 8h10"
                  stroke="rgba(20,30,49,0.45)"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Пункты активного чек-листа и композер */}
          <div className="flex flex-col gap-[6px] rounded-[18px] bg-white px-[14px] py-[12px] shadow-[0_2px_8px_rgba(20,30,49,0.05)]">
            {roots.map((it) => (
              <RChecklistRow
                key={it.id}
                item={it}
                subs={subsOf(it.id)}
                ctx={ctx}
              />
            ))}

            {activeId !== null ? (
              <div className="mb-[6px] mt-[12px] flex w-full items-start gap-[10px] rounded-[18px] bg-white py-[8px] pl-[8px] pr-[12px] shadow-[0_1px_2px_rgba(20,30,49,0.05)]">
                <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-[#57B792] shadow-[0_3px_8px_rgba(87,183,146,0.4)]">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M8 3.6v8.8M3.6 8h8.8"
                      stroke="#FFFFFF"
                      strokeWidth={2.2}
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <textarea
                  ref={newItemRef}
                  rows={1}
                  value={newItem}
                  disabled={disabled}
                  placeholder="Добавить пункт"
                  onChange={(e) => {
                    const el = e.currentTarget;
                    el.style.height = 'auto';
                    el.style.height = `${el.scrollHeight}px`;
                    setNewItem(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Tab') {
                      e.preventDefault();
                      if (!newItem.trim()) {
                        return;
                      }
                      newHoursRef.current?.focus();
                      newHoursRef.current?.select();
                    }
                  }}
                  className="relative top-[4px] min-w-0 flex-1 resize-none overflow-hidden bg-transparent font-sans text-[12px] leading-[1.45] text-[#575757] outline-none"
                />
                <span className="flex w-[56px] flex-[0_0_56px] items-center gap-[4px] rounded-[10px] bg-white px-[10px] py-[5px] shadow-[0_1px_3px_rgba(20,30,49,0.08)] transition-colors focus-within:bg-[rgba(87,183,146,0.14)]">
                  <input
                    ref={newHoursRef}
                    value={newHours}
                    disabled={disabled}
                    placeholder="0"
                    onChange={(e) => setNewHours(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        addItem();
                      }
                    }}
                    className="w-[30px] bg-transparent text-right text-[12px] font-bold text-[#141E31] outline-none"
                    aria-label="Время пункта, часов"
                  />
                  <span className="text-[11px] font-semibold text-[rgba(20,30,49,0.5)]">
                    ч
                  </span>
                </span>
                {newItem.trim() ? (
                  <button
                    type="button"
                    onClick={addItem}
                    className="whitespace-nowrap rounded-full bg-[#57B792] px-[12px] py-[5px] text-[10px] font-bold tracking-[0.3px] text-white"
                  >
                    Enter
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={addChecklist}
          className={clsx(
            'flex w-full items-center gap-[11px] rounded-[12px] bg-white px-[14px] py-[12px] text-left',
            'shadow-[0_1px_3px_rgba(20,30,49,0.08)] transition-shadow hover:shadow-[0_2px_8px_rgba(20,30,49,0.12)]',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-[#57B792] shadow-[0_2px_6px_rgba(87,183,146,0.35)]">
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden
            >
              <path
                d="M8 3.6v8.8M3.6 8h8.8"
                stroke="#FFFFFF"
                strokeWidth={2.2}
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="flex-1 text-[12px] font-semibold text-[#141E31]">
            Добавить Checklist
          </span>
        </button>
      )}
    </div>
  );
}
